import { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { api, extractErrorMessage } from '../api/client';
import { useToast } from './Toast';
import { Modal } from './Modal';
import { Button } from './Button';
import { hoyAR } from '../pages/flujo-caja/formato';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useCamposObligatorios, faltaSi } from '../hooks/useCamposObligatorios';
import { Obligatorio, LeyendaObligatorio } from './Obligatorio';

interface Cliente { id: number; nombre: string }
interface CuentaFondos { id: number; nombre: string; activa?: boolean }
/** Devolución en plata del saldo a favor (GET /clientes/:id/devoluciones-saldo). */
interface DevolucionSaldo {
  id: number;
  fecha: string;
  monto: number | string;
  medio?: string | null;
  notas?: string | null;
  usuario?: string | null;
  cuenta?: { id: number; nombre: string } | null;
  notasCredito?: Array<{ id: number; monto: number | string; notaCredito?: { id: number; numeroFactura?: string | null } | null }>;
}

const formVacio = () => ({ fecha: hoyAR(), monto: '', cuentaId: '', medio: 'transferencia', notas: '' });
const fechaCorta = (f?: string | null) => (f ? String(f).slice(0, 10).split('-').reverse().join('/') : '—');
const pesos = (n: number | string) => `$ ${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MEDIOS = [
  { valor: 'efectivo', etiqueta: 'Efectivo' },
  { valor: 'transferencia', etiqueta: 'Transferencia' },
  { valor: 'cheque', etiqueta: 'Cheque' },
  { valor: 'mercadopago', etiqueta: 'MercadoPago' },
];
const etiquetaMedio = (m?: string | null) => MEDIOS.find(x => x.valor === m)?.etiqueta ?? m ?? '';

/**
 * Devolución en plata del saldo a favor de un cliente: un movimiento aparte de la nota de crédito. Consume el saldo de
 * sus notas de la más vieja a la más nueva; no toca facturas ni la caja. Lista las devoluciones con "Revertir".
 */
export function DevolucionesSaldoModal({ open, clienteIdInicial, clientes, onClose, onCambio }: {
  open: boolean;
  clienteIdInicial?: string;
  clientes: Cliente[];
  onClose: () => void;
  /** Cambió el saldo a favor del cliente (para recargarlo afuera). */
  onCambio?: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const [clienteId, setClienteId] = useState('');
  const [form, setForm] = useState(formVacio());
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [revirtiendo, setRevirtiendo] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setClienteId(clienteIdInicial ?? '');
    setForm(formVacio());
    setError('');
  }, [open, clienteIdInicial]);
  // Solo cuenta la devolución a medio cargar (elegir otro cliente es para consultar). El formulario se limpia en
  // un efecto al abrir: se compara contra el vacío, no contra la foto al abrir.
  const cierre = useCierreConfirmado(useHayCambios(form, open, formVacio()), onClose);
  // Obligatorios de la devolución (backend: CreateDevolucionSaldoDto y crearDevolucionSaldo): el cliente, la fecha y el
  // monto (mayor a 0). La fecha futura y el tope del saldo tienen su propio aviso.
  const obligatorios = useCamposObligatorios(open);

  const { data: saldo, refetch: refetchSaldo } = useApi<{ saldoAFavor: number }>(open && clienteId ? `/api/v3/clientes/${clienteId}/saldo-a-favor` : null);
  const { data: devoluciones, loading, refetch: refetchLista } = useApi<DevolucionSaldo[]>(open && clienteId ? `/api/v3/clientes/${clienteId}/devoluciones-saldo` : null);
  const { data: cuentas, error: errorCuentas } = useApi<CuentaFondos[]>(open ? '/api/v3/finanzas/cuentas' : null);
  const disponible = Number(saldo?.saldoAFavor ?? 0);

  const recargar = () => {
    refetchSaldo();
    refetchLista();
    onCambio?.();
  };

  const registrar = async () => {
    if (!obligatorios.validar()) return;
    const monto = Math.round(Number(form.monto) * 100) / 100;
    if (form.fecha > hoyAR()) { setError('La devolución es un movimiento ya realizado: la fecha no puede ser futura'); return; }
    if (monto > disponible + 0.005) { setError(`El monto supera el saldo a favor del cliente (${pesos(disponible)})`); return; }
    setError('');
    setGuardando(true);
    try {
      await api.post(`/api/v3/clientes/${clienteId}/devoluciones-saldo`, {
        fecha: form.fecha,
        monto,
        cuentaId: form.cuentaId ? Number(form.cuentaId) : undefined,
        medio: form.medio || undefined,
        notas: form.notas.trim() || undefined,
      });
      toast.addToast(`Devolución de saldo a favor registrada: ${pesos(monto)}`, 'success');
      setForm(formVacio());
      recargar();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const revertir = async (d: DevolucionSaldo) => {
    if (!window.confirm(`¿Revertir la devolución de ${pesos(d.monto)} del ${fechaCorta(d.fecha)}? El importe vuelve al saldo a favor del cliente.`)) return;
    setRevirtiendo(d.id);
    try {
      await api.delete(`/api/v3/clientes/${clienteId}/devoluciones-saldo/${d.id}`);
      toast.addToast('Devolución revertida: el importe volvió al saldo a favor', 'success');
      recargar();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setRevirtiendo(null);
    }
  };

  const puedeCrear = can('cobros', 'crear');
  const puedeEliminar = can('cobros', 'eliminar');

  return (
    <Modal open={open} cierre={cierre} title="Devolver saldo a favor" size="md">
      <div {...obligatorios.contenedor} className="space-y-4 text-sm">
        <p className="text-slate-500">
          Registra la devolución en plata del saldo a favor que dejaron las notas de crédito del cliente. Es un movimiento
          aparte de la nota: no modifica facturas ni la caja, y el saldo de la cuenta se actualiza a mano.
        </p>
        <LeyendaObligatorio />
        <div className="grid grid-cols-2 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">Cliente<Obligatorio /></label>
            <select aria-label="Cliente" required value={clienteId} onChange={e => { setClienteId(e.target.value); setError(''); }} className="w-full px-3 py-2 border rounded-lg">
              <option value="">Elegí un cliente</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          {clienteId && (
            <p className="text-emerald-700 pb-2">{`Saldo a favor disponible: ${pesos(disponible)}`}</p>
          )}
        </div>
        {clienteId && (<>
          <div className="border rounded-lg p-3 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">Fecha<Obligatorio /></label>
                <input type="date" max={hoyAR()} aria-label="Fecha" {...faltaSi(!form.fecha)} value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} className="w-full px-2 py-2 border rounded-lg" title="La devolución ya se hizo: hoy o una fecha anterior" />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Monto<Obligatorio /></label>
                <input type="number" min="0" step="0.01" aria-label="Monto" {...faltaSi(!(Number(form.monto) > 0))} value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} className="w-full px-2 py-2 border rounded-lg" />
                {disponible > 0 && (
                  <button type="button" onClick={() => setForm({ ...form, monto: String(disponible) })} className="text-xs text-blue-600 hover:text-blue-800 mt-1">Todo el saldo</button>
                )}
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Medio</label>
                <select value={form.medio} onChange={e => setForm({ ...form, medio: e.target.value })} className="w-full px-2 py-2 border rounded-lg">
                  {MEDIOS.map(m => <option key={m.valor} value={m.valor}>{m.etiqueta}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">Cuenta de origen (opcional)</label>
                <select value={form.cuentaId} onChange={e => setForm({ ...form, cuentaId: e.target.value })} className="w-full px-2 py-2 border rounded-lg" disabled={!!errorCuentas}>
                  <option value="">Sin indicar</option>
                  {(cuentas ?? []).filter(c => c.activa !== false).map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
                {errorCuentas && <p className="text-xs text-amber-700 mt-1">No se pudo cargar la lista de cuentas (hace falta el permiso de Pagos): la devolución se registra sin cuenta.</p>}
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Notas</label>
                <input aria-label="Notas" value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} className="w-full px-2 py-2 border rounded-lg" />
              </div>
            </div>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <div className="flex justify-end">
              <Button
                onClick={registrar}
                loading={guardando}
                disabled={!puedeCrear || disponible <= 0}
                title={!puedeCrear ? 'No tenés permiso para realizar esta acción' : disponible <= 0 ? 'El cliente no tiene saldo a favor' : undefined}
              >{guardando ? 'Registrando...' : 'Registrar devolución'}</Button>
            </div>
          </div>
          <div>
            <p className="font-medium mb-1">Devoluciones registradas</p>
            {loading ? (
              <p className="text-slate-500">Cargando…</p>
            ) : (devoluciones ?? []).length === 0 ? (
              <p className="text-slate-500">El cliente no tiene devoluciones de saldo a favor.</p>
            ) : (
              <ul className="space-y-2">
                {devoluciones!.map(d => (
                  <li key={d.id} className="flex flex-wrap items-center gap-2 border-t pt-2">
                    <span className="font-medium">{pesos(d.monto)}</span>
                    <span className="text-slate-500">
                      {[fechaCorta(d.fecha), etiquetaMedio(d.medio), d.cuenta?.nombre, d.usuario].filter(Boolean).join(' · ')}
                    </span>
                    {(d.notasCredito?.length ?? 0) > 0 && (
                      <span className="text-xs text-slate-500">
                        {`de ${d.notasCredito!.map(n => `${n.notaCredito?.numeroFactura || `#${n.notaCredito?.id ?? ''}`} (${pesos(n.monto)})`).join(', ')}`}
                      </span>
                    )}
                    {d.notas && <span className="text-xs text-slate-500">{d.notas}</span>}
                    <button
                      type="button"
                      onClick={() => revertir(d)}
                      disabled={!puedeEliminar || revirtiendo === d.id}
                      title={puedeEliminar ? 'El importe vuelve al saldo a favor del cliente' : 'No tenés permiso para realizar esta acción'}
                      className="ml-auto text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >{revirtiendo === d.id ? 'Revirtiendo...' : 'Revertir'}</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>)}
      </div>
    </Modal>
  );
}
